import React, { useCallback, useMemo, useState } from 'react'
import { useHistory, useLocation, Link } from 'react-router-dom'
import { ExploreMenu } from './ExploreMenu'
import { HeaderButton } from './HeaderButton'
import { HeaderSearchForm } from './HeaderSearchForm'
import { SearchOverlay } from '@maui/duetai'
import { Box, Button, Icon, HeaderDivider, HeaderBar, Badge, Text } from '@maui/elements'
import { OrgSwitcher } from '@nautilusjs/org-switcher'
import { UpgradeIconPill } from '@maui/freemium'
import routes from '../routes/routes'
import { useAuthContext } from '../AuthContext'
import useSearchParams from '../hooks/useSearchParams'
import TrialTimer from '../common/TrialTimer'
import {
  maRolesMap,
} from '../auth/roles'
import { useDuetAiFeature } from '../hooks/useDuetAiFeature'
import { useUserPreferences } from '@nautilusjs/mati-shared'
import ClickOutsideListener from '../common/ClickOutsideListener'

const Header = () => {
  const { isGTIUX } = useAuthContext()
  return isGTIUX ? null : <HeaderInner />
}

const HeaderInner = () => {
  const {
    hasMaAdminRole,
    hasOrgAdminRole,
    hasEntitlements,
    isLoggedIn,
    isMultiOrg,
    hasDTMLicenses,
    hasThreatProfilesAccess
  } = useAuthContext()

  // Menu Items
  // ----------------
  const accountManagementRoute = routes.accountManagement()
  const reportsRoute = routes.reportsExplore()

  /**
   * These are micro-frontend routes, we can't use `useNamedRoute` hook here
   */
  const filesRoute = '/files'
  const dtmRoute = '/dtm'

  // Admin Menu Items
  const hasMaAdmin = hasMaAdminRole(maRolesMap.mandiantAdmin) || hasMaAdminRole(maRolesMap.mandiantApprover)
  const isOrgAdmin = hasOrgAdminRole()
  const showAdminFeatures = hasMaAdmin && !isOrgAdmin

  // Search
  const [{ q }] = useSearchParams()
  const history = useHistory()
  const location = useLocation()
  const [query, setQuery] = useState(q)
  const [featuresVisible, setFeaturesVisible] = useState(false)
  const { isFeatureEnabled: isDuetAiEnabled, isUserEntitled } = useDuetAiFeature()
  const { preferences = {} } = useUserPreferences()
  const { searchPreference } = preferences

  useMemo(() => setQuery(q), [q])

  const onCommit = useCallback((query) => {
    if (query) {
      history.push({
        pathname: '/search',
        search: `${searchPreference === 'gemini' ? 'geminiQuery' : 'query'}=${encodeURIComponent(query)}`,
        hash: `${searchPreference === 'gemini' ? 'gemini' : 'keyword'}`
      })
    }
    setQuery('')
  }, [history, setQuery, searchPreference])

  const onChange = useCallback((e) => {
    setQuery(e.target.value)
  }, [setQuery])

  const [showSearchOverlay, setShowSearchOverlay] = useState(false)
  const showDuetAISearchOverlay = () => { setShowSearchOverlay(true) }
  const hideDuetAISearchOverlay = () => { setShowSearchOverlay(false) }

  const onSearchOverlayClick = useCallback(() => {
    history.push(`${isUserEntitled ? '/accountmanagement' : '/upgrade'}`)
    hideDuetAISearchOverlay()
  }, [history, isUserEntitled])

  if (!isLoggedIn) {
    return null
  }

  return (
    <HeaderBar
      variant="app"
      sticky={true}
      slotLeft={
        <Text
          display='flex'
          variant='heading4'
          fontWeight='fontWeightSemiBold'
          whiteSpace='nowrap'
          flex={1}
          alignItems='center'
        >
          <Link
            to='/'
            style={{
              color: 'inherit',
              textDecoration: 'none',
              paddingRight: '16px',
              textTransform: 'uppercase',
            }}
          >
            Threat Intelligence
          </Link>
          {(featuresVisible || isMultiOrg) && <HeaderDivider />}
          <TrialTimer setFeaturesVisible={setFeaturesVisible} />
          {isMultiOrg && <OrgSwitcher />}
        </Text>
      }
    >
      {showAdminFeatures && (
        <HeaderButton to={routes.admin()}>
          Admin
        </HeaderButton>
      )}
      <HeaderButton to="/threat-profile" display="flex" gap="spaceGapMedium">
        { !hasThreatProfilesAccess && <UpgradeIconPill iconSize='sm' variant='icon' /> }
        Your Threat Profile
        <Badge
          fontSize="textBadgeFontSize"
          fontWeight="fontWeightBold"
          borderRadius="borderRadiusSmall"
          color="colorTextReversed"
          bg="colorActionSecondaryDefault"
          letterSpacing="1px"
        >
          BETA
        </Badge>
      </HeaderButton>
      <HeaderButton to={reportsRoute}>
        Reports & Analysis
      </HeaderButton>
      <ExploreMenu />
      {hasDTMLicenses && (
        <HeaderButton to={dtmRoute}>
          Digital Threat Monitoring
        </HeaderButton>
      )}
      {hasEntitlements(['INTEL_RBI_FUS', 'INTEL_RBI_OPS']) && (
        <HeaderButton to={filesRoute}>
          File Analysis
        </HeaderButton>
      )}
      <HeaderButton to={accountManagementRoute}>
        Account Settings
      </HeaderButton>
      {!location.pathname.includes('/search') && (
        (isDuetAiEnabled && searchPreference === 'gemini')
          ? (
            <Box position='relative'>
              <Button p='0' ml='spaceGapXLarge' variant='text' onClick={showDuetAISearchOverlay}>
                <Icon size='lg' name='aiSearch__uno' />
              </Button>
              {showSearchOverlay && (
                <ClickOutsideListener onClickOutside={hideDuetAISearchOverlay}>
                  <SearchOverlay
                    onSubmit={onCommit}
                    onClick={onSearchOverlayClick}
                    hasGeminiAccess={isUserEntitled}
                    top='5px'
                    right='5px'
                  />
                </ClickOutsideListener>
              )}
            </Box>
            )
          : (
            <HeaderSearchForm
              onCommit={onCommit}
              onChange={onChange}
              value={query}
            />
            )
      )}
    </HeaderBar>
  )
}

export default Header
